import type { TourId } from '@sorrem/shared';
import type { TourTarget } from '@sorrem-client/ui/shared/tour-targets';

/** Where a step's tooltip sits against what it points at. */
export type TourStepPlacement = 'top' | 'bottom' | 'left' | 'right' | 'auto';

/** One step of a guided tour. */
export interface TourStepDefinition {
    /** The `data-tour` mark it points at; left out, it sits centered on the screen. A step whose mark isn't there is passed over. */
    readonly target?: TourTarget;
    readonly title: string;
    /** What it says. `{key:<binding id>}` names the player's own key for that input; `{skill:<skill id>}` the key of the slot that skill is on. */
    readonly body: string;
    readonly placement?: TourStepPlacement;
}

export interface TourDefinition {
    /** Raise it when the steps change, so accounts that finished the old tour see the new one. */
    readonly version: number;
    readonly steps: readonly TourStepDefinition[];
}

/**
 * Every guided tour's steps (#234). Character Home's page tour runs first;
 * each tab's tour waits for it, and shows the first time its tab opens.
 */
export const TOURS: Readonly<Record<TourId, TourDefinition>> = {
    'character-home': {
        version: 1,
        steps: [
            {
                target: 'tabs',
                title: 'Your character\'s home',
                body: 'Everything for your character lives in these tabs. Each one has a short tour of its own the first time it opens.',
                placement: 'bottom',
            },
            {
                title: 'The room',
                body: 'Each tab has its object in the room, lit while you hover the tab: the map for Crucibles, the workbench for Crafting, the chest and gear stand for Inventory, the writing table for Journey, and the window for Skills.',
            },
            {
                target: 'character',
                title: 'Your character',
                body: 'Your character\'s name. The arrow beside it switches characters, or makes a new one.',
            },
            {
                target: 'mastery-dials',
                title: 'Mastery',
                body: 'Veil Attunement, Crafting and Martial Discipline. Each grows as you use it: Essence abilities, crafting, and fighting with your weapons.',
            },
            {
                target: 'attributes',
                title: 'Attributes and core stats',
                body: 'Your attributes shape your core stats. The reshape button beside them reassigns your attributes, paid with banked essence.',
            },
            {
                target: 'loadout',
                title: 'Loadout',
                body: 'What you wield and wear: your weapons on the left, armor and accessories on the right.',
                placement: 'top',
            },
            {
                target: 'essence-bar',
                title: 'Essence',
                body: 'Essence is your life, and what Essence abilities spend. Fallen manifestations leave more of it behind.',
                placement: 'top',
            },
            {
                target: 'action-bar',
                title: 'Action bar',
                body: 'Draw sits on {skill:attune}. Right-click any action slot to choose an Essence ability for it.',
                placement: 'top',
            },
            {
                target: 'vessel',
                title: 'Your vessel',
                body: 'Where your banked essence is kept. It pays for crafting, for reshaping attributes, and for crystals coalesced before a Crucible.',
                placement: 'top',
            },
            {
                target: 'header-account',
                title: 'Settings and your account',
                body: 'Settings, notifications and your account menu. Settings can replay these tours whenever you like.',
                placement: 'bottom',
            },
        ],
    },
    'tab-overview': {
        version: 1,
        steps: [
            {
                target: 'commissions',
                title: 'Commissions',
                body: 'What the House of Exchange has asked of you. Accept a commission to enter its Crucible.',
            },
            {
                target: 'featured-crucibles',
                title: 'Featured Crucibles',
                body: 'Crucibles worth a look. Pick one to see it on the Crucibles tab.',
            },
        ],
    },
    'tab-inventory': {
        version: 1,
        steps: [
            {
                target: 'loadout',
                title: 'Equipped',
                body: 'Your gear. Drag an item onto a frame to equip it.',
                placement: 'top',
            },
            {
                target: 'carried',
                title: 'Carried',
                body: 'What you take into a Crucible. Strength, your gear and your account add slots. Click a slot\'s key plate, then press an action key, to use it from the action bar.',
            },
            {
                target: 'storage',
                title: 'Storage cache',
                body: 'What stays home. Select any item to see it in the item panel, where you can compare, equip or trade it.',
            },
        ],
    },
    'tab-skills': {
        version: 1,
        steps: [
            {
                title: 'The skill tree',
                body: 'Its Martial, Essence and Crafting arms branch out from the middle. Drag to move around it, and zoom with the wheel or the zoom controls.',
            },
            {
                target: 'skills-panel',
                title: 'Skills',
                body: 'Select a skill to see its details: its rings, and the skill points that raise them.',
            },
            {
                title: 'Essence abilities',
                body: 'On an Essence skill, choose Assign to a hotkey to put its ability on your action bar. You can also right-click an action slot to choose one.',
            },
        ],
    },
    'tab-crucibles': {
        version: 1,
        steps: [
            {
                target: 'crucibles-locked',
                title: 'Not yet',
                body: 'Crucibles open up here once you complete the House of Exchange\'s commission.',
            },
            {
                target: 'crucibles-list',
                title: 'Crucibles',
                body: 'Crucibles that have surfaced and are open to enter. Select one to see it.',
            },
            {
                target: 'crucibles-actions',
                title: 'Create or join',
                body: 'Create a Crucible of your own (Faint ones at first), or join a friend\'s with their invite code.',
                placement: 'bottom',
            },
            {
                target: 'crucible-info',
                title: 'The Crucible',
                body: 'Its Veil Saturation and what that changes, and the readiness it recommends against your own.',
            },
        ],
    },
    'tab-crafting': {
        version: 1,
        steps: [
            {
                target: 'crafting-items',
                title: 'Your items',
                body: 'Equipped, Carried and Storage: pick the item to work on.',
            },
            {
                target: 'crafting-workbench',
                title: 'The workbench',
                body: 'Upgrade the item or reroll it here.',
            },
            {
                target: 'crafting-vessel',
                title: 'Costs',
                body: 'Crafting is paid from your banked essence, and each cost shows the essence types it takes.',
            },
        ],
    },
    'tab-journey': {
        version: 1,
        steps: [
            {
                target: 'journey-books',
                title: 'Your Journal',
                body: 'The Journal keeps your story: the commissions you take and the scenes you have watched, to read or replay.',
                placement: 'bottom',
            },
        ],
    },
    // Follows the Journey tour once the Compendium opens, at the end of the Mill Road commission.
    'journey-compendium': {
        version: 1,
        steps: [
            {
                target: 'journey-books',
                title: 'Your Compendium',
                body: 'The Compendium keeps the visions you have seen, so you can relive them and piece together whose they were.',
                placement: 'bottom',
            },
        ],
    },
    'gathering': {
        version: 1,
        steps: [
            {
                title: 'The gathering',
                body: 'Your party gathers here, in an arc before the Crucible\'s opening.',
            },
            {
                target: 'hud-party',
                title: 'Your party',
                body: 'Who has gathered, your Ready toggle, and the Chat tab.',
            },
            {
                target: 'hud-commission',
                title: 'The Crucible',
                body: 'Its Veil Saturation and modifiers, how many are ready, and the invite code to bring others.',
            },
            {
                target: 'loadout',
                title: 'Loadout and Carried',
                body: 'Your gear can still change here. Crystals coalesced now are ready the moment you enter, paid from banked essence when the run starts.',
                placement: 'top',
            },
            {
                target: 'ready-toggle',
                title: 'Ready',
                body: 'Once everyone is ready, a countdown above the opening leads the party in. Un-ready to stop it.',
            },
        ],
    },
    'run-hud': {
        version: 1,
        steps: [
            {
                title: 'Moving',
                body: 'Click the ground to walk there. Hold {key:force-move} to walk toward the pointer whatever is under it, and press {key:dodge} to dodge.',
            },
            {
                title: 'Attacking',
                body: 'Click an enemy to attack it. {key:aimed-attack} swings at the pointer without moving, and {key:offhand-attack} strikes with your off hand.',
            },
            {
                target: 'essence-bar',
                title: 'Essence',
                body: 'Your life, and what Essence abilities spend. Collect more from fallen manifestations.',
                placement: 'top',
            },
            {
                target: 'action-bar',
                title: 'Action bar',
                body: 'Draw is on {skill:attune}. Right-click a slot to choose an Essence ability, then press its key.',
                placement: 'top',
            },
            {
                target: 'hud-carried',
                title: 'Carried',
                body: 'Open it with {key:toggle-inventory}. Crystals you coalesce from essence wait here; click a slot\'s key plate to link it to an action key.',
            },
            {
                target: 'hud-commission',
                title: 'Your commission',
                body: 'Its goals: gather the essence, then shatter the heart of the Crucible.',
            },
            {
                target: 'hud-map',
                title: 'Mini map',
                body: 'The areas you know, and the heart. {key:toggle-map} shows or hides it.',
            },
            {
                target: 'settings',
                title: 'Leaving and reporting',
                body: '{key:open-menu} asks whether to leave the Crucible, and {key:report-bug} reports a bug.',
            },
        ],
    },
};
